import { ReactNode } from "react"
import { Link, usePage } from "@inertiajs/react"
import { PageProps } from "@inertiajs/core"
import AppLayout from "@/layouts/AppLayout"
import DashboardTopbar from "@/components/DashboardTopbar"
import { BarChart } from "@tremor/react"
import { ChartPieIcon } from "@heroicons/react/24/outline"

type RecordByColor = {
  games: number
  wins: number
  losses: number
  draws: number
}

type StatsProps = {
  player_name: string | null
  record: {
    total: number
    wins: number
    losses: number
    draws: number
    win_rate: number | null
    as_white: RecordByColor
    as_black: RecordByColor
  } | null
  opponents: Array<{
    id: string
    name: string
    games: number
    wins: number
    losses: number
    draws: number
  }> | null
  events: Array<{
    id: string
    name: string
    games: number
    wins: number
    losses: number
    draws: number
  }> | null
  activity: Array<{
    month: string
    games: number
  }> | null
}

const Pill = ({
  value,
  label,
  color,
}: {
  value: number
  label: string
  color: "green" | "red" | "gray"
}) => {
  const colors = {
    green: "bg-green-50 text-green-700",
    red: "bg-red-50 text-red-700",
    gray: "bg-gray-100 text-gray-500",
  }

  return (
    <span
      className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${colors[color]}`}
    >
      {value}{label}
    </span>
  )
}

const WLD = ({
  wins,
  losses,
  draws,
}: {
  wins: number
  losses: number
  draws: number
}) => (
  <div className="flex items-center gap-1">
    <Pill value={wins} label="W" color="green" />
    <Pill value={losses} label="L" color="red" />
    <Pill value={draws} label="D" color="gray" />
  </div>
)

const ColorRecord = ({
  label,
  data,
}: {
  label: string
  data: RecordByColor
}) => (
  <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-b border-gray-100 py-3 last:border-0">
    <span className="text-sm font-medium text-gray-700">{label}</span>
    <div className="flex items-center gap-x-4">
      <span className="text-sm text-gray-500">{data.games} games</span>
      <WLD wins={data.wins} losses={data.losses} draws={data.draws} />
    </div>
  </div>
)

const Section = ({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) => (
  <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-xs">
    <h2 className="text-base font-semibold text-gray-900">{title}</h2>
    {children}
  </div>
)

const StatsPage = () => {
  const { record, opponents, events, activity } =
    usePage<PageProps & StatsProps>().props

  if (!record) {
    return (
      <>
        <DashboardTopbar title="My Stats" />
        <div className="padding mx-auto flex max-w-xs flex-col items-center justify-center py-16">
          <div className="flex items-center justify-center rounded-xl bg-gray-200 p-3">
            <ChartPieIcon className="h-6 w-6 text-gray-500" />
          </div>
          <h3 className="mt-4 text-center text-base leading-6 font-semibold text-gray-900">
            Set up My Player
          </h3>
          <p className="mt-2 text-center text-sm text-gray-500">
            To view your personal stats, link yourself to a player in Settings.
          </p>
          <Link
            href="/settings"
            className="text-chessdrop-600 hover:text-chessdrop-500 mt-4 text-sm font-medium"
          >
            Go to Settings
          </Link>
        </div>
      </>
    )
  }

  const hasGames = record.total > 0

  return (
    <>
      <DashboardTopbar title="My Stats" />

      {!hasGames ? (
        <div className="padding mx-auto flex max-w-xs flex-col items-center justify-center py-16">
          <div className="flex items-center justify-center rounded-xl bg-gray-200 p-3">
            <ChartPieIcon className="h-6 w-6 text-gray-500" />
          </div>
          <h3 className="mt-4 text-center text-base leading-6 font-semibold text-gray-900">
            No stats yet
          </h3>
          <p className="mt-1 text-center text-sm text-gray-500">
            Your stats will appear here after you save some games with yourself
            as a player.
          </p>
        </div>
      ) : (
        <div className="padding mx-auto w-full max-w-4xl space-y-6 py-6">
          {/* Record Overview */}
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-xs">
              <h2 className="text-sm font-medium text-gray-500">
                Games Played
              </h2>
              <p className="mt-2 text-3xl font-semibold tracking-tight text-gray-900">
                {record.total}
              </p>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-xs">
              <h2 className="text-sm font-medium text-gray-500">Win Rate</h2>
              <p className="mt-2 text-3xl font-semibold tracking-tight text-gray-900">
                {record.win_rate !== null ? `${record.win_rate}%` : "—"}
              </p>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-xs">
              <h2 className="text-sm font-medium text-gray-500">Record</h2>
              <div className="mt-3">
                <WLD
                  wins={record.wins}
                  losses={record.losses}
                  draws={record.draws}
                />
              </div>
            </div>
          </div>

          {/* By Color — merged into record section */}
          <Section title="By Color">
            <div className="mt-2">
              <ColorRecord label="As White" data={record.as_white} />
              <ColorRecord label="As Black" data={record.as_black} />
            </div>
          </Section>

          {/* Activity Chart */}
          {activity && activity.some((m) => m.games > 0) ? (
            <Section title="Activity (Last 12 months)">
              <div className="mt-4 h-48">
                <BarChart
                  data={activity}
                  index="month"
                  categories={["games"]}
                  colors={["blue"]}
                  startEndOnly
                  showLegend={false}
                  yAxisWidth={30}
                  className="h-full w-full"
                />
              </div>
            </Section>
          ) : null}

          {/* Opponents */}
          {opponents && opponents.length > 0 ? (
            <Section title="Most-Played Opponents">
              <div className="mt-4 divide-y divide-gray-100">
                {opponents.map((opp) => (
                  <div
                    key={opp.id}
                    className="flex flex-col gap-1 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-x-4"
                  >
                    <Link
                      href={`/players/${opp.id}`}
                      className="text-sm font-medium text-gray-900 hover:underline sm:min-w-0 sm:truncate"
                      title={opp.name}
                    >
                      {opp.name}
                    </Link>
                    <div className="flex items-center gap-x-4">
                      <span className="text-sm text-gray-500">
                        {opp.games} game{opp.games === 1 ? "" : "s"}
                      </span>
                      <WLD
                        wins={opp.wins}
                        losses={opp.losses}
                        draws={opp.draws}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          ) : null}

          {/* Events */}
          {events && events.length > 0 ? (
            <Section title="Events">
              <div className="mt-4 divide-y divide-gray-100">
                {events.map((event) => (
                  <div
                    key={event.id}
                    className="flex flex-col gap-1 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-x-4"
                  >
                    <span className="text-sm font-medium text-gray-900 sm:min-w-0 sm:truncate" title={event.name}>
                      {event.name}
                    </span>
                    <div className="flex items-center gap-x-4">
                      <span className="text-sm text-gray-500">
                        {event.games} game{event.games === 1 ? "" : "s"}
                      </span>
                      <WLD
                        wins={event.wins}
                        losses={event.losses}
                        draws={event.draws}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          ) : null}
        </div>
      )}
    </>
  )
}

export default StatsPage

StatsPage.layout = (page: ReactNode) => <AppLayout>{page}</AppLayout>
